/**
 * DomainTags — filter pills for switching between active domains.
 *
 * Top-level domains are shown as pills. If a top-level domain has subdomains,
 * clicking it expands a second row of subdomain pills rather than activating
 * the parent directly. The parent pill is highlighted when any of its children
 * is the active domain.
 *
 * Reads from useDomainStore only — does NOT subscribe to Firestore.
 */

import { useState } from 'react';
import { ChevronDownIcon } from '@heroicons/react/20/solid';
import { useDomainStore } from '../../../stores/domainStore';
import { useSchool } from '../../../contexts/SchoolContext';
import { domainSupportsSurface } from '../../../services/domain/centralModel';
import DomainRegistryNotice from './DomainRegistryNotice';

export function DomainTags({ className = '', surface = null }) {
  const { domains, activeDomain, isLoaded, registryError, setActiveDomain } = useDomainStore();
  const { schoolId } = useSchool();
  const [expandedParentId, setExpandedParentId] = useState(null);

  if (!isLoaded) {
    if (registryError) return <DomainRegistryNotice />;
    return (
      <div className={`flex gap-2 ${className}`} aria-busy="true">
        <div className="skeleton h-8 w-20 rounded-full" />
        <div className="skeleton h-8 w-20 rounded-full" />
      </div>
    );
  }

  const activeDomains = domains.filter(d =>
    d.active !== false
    && d.protected !== true
    && (!surface || domainSupportsSurface(d, surface)));
  const topLevel = activeDomains.filter(d => !d.parentDomainId);

  if (topLevel.length === 0) return null;

  const subdomainsFor = (parentId) => activeDomains.filter(d => d.parentDomainId === parentId);

  const activeParentId = activeDomain?.parentDomainId ?? activeDomain?.id ?? null;

  const handleTopLevelClick = (domain) => {
    const subs = subdomainsFor(domain.id);
    if (subs.length > 0) {
      // Toggle expansion; deactivate any active subdomain/parent when collapsing
      if (expandedParentId === domain.id) {
        setExpandedParentId(null);
        if (activeParentId === domain.id) setActiveDomain(null, schoolId);
      } else {
        setExpandedParentId(domain.id);
      }
    } else {
      // No subdomains — toggle activation directly
      setExpandedParentId(null);
      setActiveDomain(activeDomain?.id === domain.id ? null : domain, schoolId);
    }
  };

  const handleSubdomainClick = (sub) => {
    setActiveDomain(activeDomain?.id === sub.id ? null : sub, schoolId);
  };

  const pillBase = 'px-4 py-1.5 rounded-full text-sm font-medium transition-all flex items-center gap-1';
  const pillActive = 'bg-primary text-primary-content shadow-sm';
  const pillInactive = 'bg-base-200 text-base-content/60 hover:bg-base-300 hover:text-base-content/80';

  return (
    <div role="tablist" className={`flex flex-col gap-2 ${className}`}>
      {/* Top-level domain pills */}
      <div className="flex gap-2 flex-wrap">
        {topLevel.map((domain) => {
          const subs = subdomainsFor(domain.id);
          const hasSubs = subs.length > 0;
          const isParentActive = activeParentId === domain.id;
          const isDirectlyActive = !hasSubs && activeDomain?.id === domain.id;
          const isHighlighted = isParentActive || isDirectlyActive;
          const isExpanded = expandedParentId === domain.id;

          return (
            <button
              key={domain.id}
              role="tab"
              aria-selected={isHighlighted}
              aria-expanded={hasSubs ? isExpanded : undefined}
              onClick={() => handleTopLevelClick(domain)}
              className={`${pillBase} ${isHighlighted ? pillActive : pillInactive}`}
            >
              {domain.label}
              {hasSubs && (
                <ChevronDownIcon
                  className={`w-3.5 h-3.5 transition-transform ${isExpanded ? 'rotate-180' : ''}`}
                />
              )}
            </button>
          );
        })}
      </div>

      {/* Subdomain row — shown when a parent is expanded */}
      {expandedParentId && (
        <div className="flex gap-2 flex-wrap pl-2 border-l-2 border-primary/30">
          {subdomainsFor(expandedParentId).map((sub) => {
            const isActive = activeDomain?.id === sub.id;
            return (
              <button
                key={sub.id}
                role="tab"
                aria-selected={isActive}
                onClick={() => handleSubdomainClick(sub)}
                className={`${pillBase} text-xs py-1 px-3 ${isActive ? pillActive : pillInactive}`}
              >
                {sub.label}
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
}

export default DomainTags;
